import { useEffect, useMemo, useRef } from 'react';
import { useViewportSize } from '../../..';
import { spacing } from '../../../styles/spacing.css';
import { getPixelValue } from '../../../utils/units';
import { SELECT_ITEM_HEIGHT, SELECT_ITEM_WITH_SECONDARY_LABEL_HEIGHT } from '../../Select/Item.css';
import { POPOVER_OFFSET } from '../../Select/SelectContent';
import { TRIGGER_PADDING_INLINE_END } from '../../Select/Trigger.css';
import * as styles from '../AutocompleteOptions.css';
import type { AdditionalActionProps, AutocompleteItemProps, NotFoundElementProps } from './useAutocomplete';

const CONTAINER_TOP_BOTTOM_PADDING = getPixelValue(styles.LIST_CONTAINER_PADDING) * 2;
const CONTAINER_BORDERS = 2; // border 1px * 2
const ANCHOR_OFFSET = -1; // we remove 1px to align the popover with the trigger (-1px is the border)

const calculateTextWidth = (text: string, fontSize: number): number => {
  // avgCharWidthRatio values represent the average character width ratios for the Lato font sizes (14px and 16px)
  const avgCharWidthRatio = {
    14: 0.54,
    16: 0.545,
  };

  const ratio = avgCharWidthRatio[fontSize as keyof typeof avgCharWidthRatio];

  return text.length * fontSize * ratio;
};

interface UseAutocompleteOptionsProps {
  readonly options: AutocompleteItemProps[];
  readonly maxVisibleContentItems: number;
  readonly showNotFound?: boolean;
  readonly notFoundProps?: NotFoundElementProps;
  readonly showAdditionalAction?: boolean;
  readonly additionalActionProps?: AdditionalActionProps;
  readonly modal?: boolean;
}

interface UseAutocompleteOptionsReturn {
  readonly ref: React.RefObject<HTMLElement>;
  readonly numberOfItemsToDisplay: number;
  readonly width: string;
  readonly optionsProps: {
    readonly modal: boolean;
    readonly anchor?: {
      readonly gap: number;
      readonly to: 'bottom start';
      readonly offset: number;
      readonly padding: string;
    };
    readonly maxHeight: number;
  };
  readonly longestLabelWidth: number;
  readonly shouldShowList: boolean;
}

export const useAutocompleteOptions = ({
  options,
  maxVisibleContentItems,
  showNotFound,
  showAdditionalAction,
  modal = true,
}: UseAutocompleteOptionsProps): UseAutocompleteOptionsReturn => {
  const ref = useRef<HTMLElement>(null);
  const { isMobileViewport } = useViewportSize();

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const numberOfItemsToDisplay = options.length + (showNotFound || showAdditionalAction ? 1 : 0);

  const height = useMemo(() => {
    const optionsWithSecondaryLabel = options.some(item => item.secondaryLabel !== undefined);
    const itemHeight = getPixelValue(
      optionsWithSecondaryLabel ? SELECT_ITEM_WITH_SECONDARY_LABEL_HEIGHT : SELECT_ITEM_HEIGHT,
    );
    const visibleItemsCount = Math.min(maxVisibleContentItems, numberOfItemsToDisplay);
    const totalItemsHeight = itemHeight * visibleItemsCount;
    const totalContainerSpacing = CONTAINER_TOP_BOTTOM_PADDING + CONTAINER_BORDERS;

    return totalItemsHeight + totalContainerSpacing;
  }, [options, maxVisibleContentItems, numberOfItemsToDisplay]);

  const width = useMemo(
    () =>
      isMobileViewport
        ? '100%'
        : `calc(${styles.INPUT_WIDTH} + ${styles.BUTTON_WIDTH} + ${TRIGGER_PADDING_INLINE_END} + ${CONTAINER_BORDERS}px)`,
    [isMobileViewport],
  );

  useEffect(() => {
    /**
     * There's a bug in the component when the trigger is at the very bottom of the screen
     * (e.g. in a dialog on mobile) - the list is displayed at the bottom of the trigger
     * even though there's no space. By deleting max-height, it gets recalculated by the
     * component and correctly displays the list above the trigger.
     */
    const element = ref.current;

    if (element?.style.maxHeight !== undefined) {
      element.style.removeProperty('max-height');
    }
  }, [height]);

  // Headless-ui virtualized list item forces transform to translate and position absolute on the item
  // see in their github repo src/components/combobox/combobox.tsx#L219,
  // Being in position absolute, the parent container no longer takes its size into account when calculating its own dimensions.
  // That's why we need to calculate the maxWidth by calculating it as the longest label width
  const longestLabelWidth = useMemo(() => {
    if (options.length === 0) return 0;

    const longestMainLabel = options.reduce((longest, current) =>
      current.label.length > longest.label.length ? current : longest,
    ).label;

    const secondaryLabels = options
      .map(option => option.secondaryLabel)
      .filter((label): label is string => label != null);

    const longestSecondaryLabel =
      secondaryLabels.length > 0
        ? secondaryLabels.reduce((longest, current) => (current.length > longest.length ? current : longest))
        : '';

    const maxMainLabelWidth = calculateTextWidth(longestMainLabel, 16);
    const maxSecondaryLabelWidth = longestSecondaryLabel.length > 0 ? calculateTextWidth(longestSecondaryLabel, 14) : 0;

    return Math.max(maxMainLabelWidth, maxSecondaryLabelWidth);
  }, [options]);

  const optionsProps = modal
    ? {
        modal: true,
        anchor: {
          gap: POPOVER_OFFSET,
          to: 'bottom start',
          offset: ANCHOR_OFFSET,
          padding: spacing.s24,
        } as const,
        maxHeight: height,
      }
    : {
        modal: false,
        maxHeight: height,
      };

  return {
    ref,
    numberOfItemsToDisplay,
    width,
    optionsProps,
    longestLabelWidth,
    shouldShowList: numberOfItemsToDisplay > 0 || isMobileViewport,
  };
};
